import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  LoadingState,
  StatePanel,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  V2Table,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { ApiError, apiGet, apiPost } from '../../lib/apiClient.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { triggerBlobDownload } from '../../lib/dataImport/xlsxWorkbook.js';
import { recordImportHistory } from '../../lib/dataImport/localHistory.js';
import {
  buildDefaultDatasetMapping,
  datasetFieldLabel,
  readDatasetFileHeaders,
  validateDatasetMapping,
} from '../../lib/dataImport/datasetMapping.js';
import { buildPartyTemplateBlob } from '../../lib/dataImport/datasetTemplate.js';
import type {
  DatasetCommitResult,
  DatasetDryRun,
  DatasetEntitiesResponse,
  DatasetEntity,
  DatasetJob,
  DatasetRow,
  DatasetRowsPage,
} from '../../lib/types/dataImportExport.js';

import { DatasetJevMapping } from './DatasetJevMapping.js';
import { DatasetWorkbookFlow } from './DatasetWorkbookFlow.js';

type Step = 'select' | 'review' | 'result';

/**
 * Entity classes the API supports but the UI deliberately keeps closed.
 * Chart-of-accounts import stays hidden until ERP-TBD-1b lands: the product
 * decision (2026-10-05, docs/agents/handoffs/ERP-TBD-1.md) is that a chart
 * import never merges and whole-chart replacement is not enabled yet.
 */
const UI_CLOSED: ReadonlySet<string> = new Set(['coa-account']);

/**
 * UI_REFERENCE_SOURCE: extends the "ورود و خروج داده" task page (no design
 * board page) over the real ERP-138 job API, `apps/api/src/import/controller.ts`.
 * Every action here is a real server request: the dry-run is server-side and
 * writes nothing, and "ثبت نهایی" is confirm + commit against the previewed
 * counts. Only entity classes the server reports as `ready` AND `permitted`
 * can be chosen; blocked/deferred ones are listed with the server's reason
 * rather than hidden or faked.
 * state reference: ready · loading · empty · permission_denied · network_error
 */
export function DatasetImportView({
  slug,
  base,
  onBack,
}: {
  readonly slug: string;
  readonly base: string;
  readonly onBack: () => void;
}) {
  const entities = useApiGet<DatasetEntitiesResponse>(`${base}/api/v1/import/entities`, [slug]);
  const [workbookMode, setWorkbookMode] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {entities.status === 'loading' ? <LoadingState /> : null}
      {entities.status === 'error' ? (
        <StatePanel
          variant={entities.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            entities.error.status === 403
              ? 'دسترسی ندارید'
              : 'ارتباط با سرور برقرار نشد؛ دوباره تلاش کنید'
          }
        />
      ) : null}
      {entities.status === 'ready' ? (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <V2Button variant="secondary" onClick={() => setWorkbookMode(false)}>
              ورود تک‌جدولی
            </V2Button>
            <V2Button variant="secondary" onClick={() => setWorkbookMode(true)}>
              ورود چندشیتی
            </V2Button>
          </div>
          {workbookMode ? (
            <DatasetWorkbookFlow base={base} data={entities.data} onBack={onBack} />
          ) : (
            <Flow slug={slug} base={base} data={entities.data} onBack={onBack} />
          )}
        </>
      ) : null}
    </div>
  );
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return 'برای این عملیات دسترسی ندارید.';
    if (error.status === 413) return 'حجم فایل بیش از حد مجاز است.';
    const detail = error.envelope.error.details?.[0]?.message;
    return detail ? `${error.envelope.error.message} (${detail})` : error.envelope.error.message;
  }
  return 'ارتباط با سرور برقرار نشد؛ دوباره تلاش کنید.';
}

async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function sampleCsvBlob(entity: DatasetEntity): Blob {
  // UTF-8 BOM so Excel opens Persian text correctly; the server strips it.
  return new Blob(['﻿' + entity.writableFields.join(',') + '\n'], {
    type: 'text/csv;charset=utf-8',
  });
}

function Flow({
  slug,
  base,
  data,
  onBack,
}: {
  readonly slug: string;
  readonly base: string;
  readonly data: DatasetEntitiesResponse;
  readonly onBack: () => void;
}) {
  const [step, setStep] = useState<Step>('select');
  const [entityClass, setEntityClass] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [jevBusy, setJevBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<DatasetJob | null>(null);
  const [dryRun, setDryRun] = useState<DatasetDryRun | null>(null);
  const [problemRows, setProblemRows] = useState<readonly DatasetRow[]>([]);
  const [result, setResult] = useState<DatasetCommitResult | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [headers, setHeaders] = useState<readonly string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [sheetCount, setSheetCount] = useState(1);
  const [reading, setReading] = useState(false);
  const fileReadGeneration = useRef(0);
  // One key per chosen file: a retry after a dropped response replays the same job.
  const idempotencyKey = useRef<string>(crypto.randomUUID());
  const inputRef = useRef<HTMLInputElement>(null);

  const entity = data.items.find((e) => e.entityClass === entityClass) ?? null;
  const maxMb = Math.floor(data.maxSourceFileBytes / (1024 * 1024));
  const hasBlocking = dryRun !== null && dryRun.errorCount > 0;
  const allOrNothingBlocked = entity?.commitStrategy === 'all-or-nothing' && hasBlocking;
  const mappingErrors =
    entity && file
      ? validateDatasetMapping(mapping, headers, entity.writableFields, entity.requiredFields)
      : [];
  const ignoredHeaders = headers.filter((header) => !Object.values(mapping).includes(header));

  function reset() {
    setStep('select');
    setFile(null);
    setJob(null);
    setDryRun(null);
    setProblemRows([]);
    setResult(null);
    setError(null);
    setAcknowledged(false);
    setHeaders([]);
    setMapping({});
    setSheetCount(1);
    setReading(false);
    fileReadGeneration.current += 1;
    idempotencyKey.current = crypto.randomUUID();
    if (inputRef.current) inputRef.current.value = '';
  }

  async function onFileChosen(chosen: File) {
    const generation = ++fileReadGeneration.current;
    setError(null);
    setFile(null);
    setHeaders([]);
    setMapping({});
    setReading(false);
    const lower = chosen.name.toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.xlsx')) {
      setError('فقط فایل csv. یا xlsx. پذیرفته می‌شود.');
      return;
    }
    if (chosen.size > data.maxSourceFileBytes) {
      setError(`حجم فایل بیش از ${maxMb.toLocaleString('fa-IR')} مگابایت است.`);
      return;
    }
    if (!entity) return;
    setReading(true);
    try {
      const parsed = await readDatasetFileHeaders(chosen);
      if (generation !== fileReadGeneration.current) return;
      idempotencyKey.current = crypto.randomUUID();
      setHeaders(parsed.headers);
      setMapping(buildDefaultDatasetMapping(parsed.headers, entity.writableFields));
      setSheetCount(parsed.sheetCount);
      setFile(chosen);
    } catch (e) {
      if (generation === fileReadGeneration.current) {
        setError(e instanceof Error ? e.message : 'خواندن ستون‌های فایل ممکن نشد.');
      }
    } finally {
      if (generation === fileReadGeneration.current) setReading(false);
    }
  }

  function onMappingChanged(field: string, source: string) {
    setMapping((current) => {
      const next = { ...current };
      if (source) next[field] = source;
      else delete next[field];
      return next;
    });
    // Mapping is part of the immutable job snapshot, so an edited mapping is a new job.
    idempotencyKey.current = crypto.randomUUID();
    setJob(null);
    setError(null);
  }

  async function onCheck() {
    if (!file || !entity || busy || jevBusy || reading || mappingErrors.length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const created = await apiPost<DatasetJob>(
        `${base}/api/v1/import/jobs`,
        { entityClass, fileName: file.name, contentBase64: await toBase64(file), mapping },
        { headers: { 'idempotency-key': idempotencyKey.current } },
      );
      setJob(created);
      const summary = await apiPost<DatasetDryRun>(
        `${base}/api/v1/import/jobs/${created.importJobId}/dry-run`,
      );
      setDryRun(summary);
      if (summary.errorCount + summary.warningCount > 0) {
        const [errs, warns] = await Promise.all([
          apiGet<DatasetRowsPage>(
            `${base}/api/v1/import/jobs/${created.importJobId}/rows?status=error&limit=100`,
          ),
          apiGet<DatasetRowsPage>(
            `${base}/api/v1/import/jobs/${created.importJobId}/rows?status=warning&limit=100`,
          ),
        ]);
        setProblemRows([...errs.items, ...warns.items]);
      } else {
        setProblemRows([]);
      }
      setStep('review');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  async function onCommit() {
    if (!job || !dryRun || !entity) return;
    setBusy(true);
    setError(null);
    try {
      await apiPost<DatasetJob>(`${base}/api/v1/import/jobs/${job.importJobId}/confirm`, {
        previewToken: dryRun.previewToken,
        acknowledgedErrorCount: dryRun.errorCount,
        acknowledgedWarningCount: dryRun.warningCount,
      });
      const committed = await apiPost<DatasetCommitResult>(
        `${base}/api/v1/import/jobs/${job.importJobId}/commit`,
      );
      setResult(committed);
      recordImportHistory(slug, {
        domainLabel: entity.label,
        importedCount: committed.committedCount,
        skippedCount: committed.skippedCount,
        errorCount: dryRun.errorCount,
      });
      setStep('result');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  const rowColumns: readonly V2TableColumn<{
    id: string;
    row: number;
    level: 'error' | 'warning';
    text: string;
  }>[] = [
    { key: 'row', header: 'ردیف', render: (r) => <span className="sk-num">{r.row}</span> },
    {
      key: 'level',
      header: 'نوع',
      render: (r) => (
        <V2Badge tone={r.level === 'error' ? 'danger' : 'warn'}>
          {r.level === 'error' ? 'خطا' : 'هشدار'}
        </V2Badge>
      ),
    },
    { key: 'text', header: 'پیام', render: (r) => <span>{r.text}</span> },
  ];
  const problemTableRows = useMemo(
    () =>
      problemRows.flatMap((row) => [
        ...row.errors.map((d, i) => ({
          id: `${row.sourceRow}-e${i}`,
          row: row.sourceRow,
          level: 'error' as const,
          text: d.field ? `${d.field}: ${d.message}` : d.message,
        })),
        ...row.warnings.map((d, i) => ({
          id: `${row.sourceRow}-w${i}`,
          row: row.sourceRow,
          level: 'warning' as const,
          text: d.field ? `${d.field}: ${d.message}` : d.message,
        })),
      ]),
    [problemRows],
  );

  const isOpen = (e: DatasetEntity) =>
    e.availability === 'ready' && e.permitted && !UI_CLOSED.has(e.entityClass);
  const selectable = data.items.filter(isOpen);
  const unavailable = data.items.filter((e) => !isOpen(e));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {step === 'select' ? (
        <V2Card>
          <V2CardHeader title="ورود دسته‌ای داده" />
          <div
            style={{ padding: '4px 20px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            {selectable.length === 0 ? (
              <V2EmptyState
                icon="upload"
                title="دسترسی ورود داده ندارید"
                description="برای ورود دسته‌ای داده با مدیر سازمان هماهنگ کنید."
              />
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {selectable.map((e) => (
                    <V2Button
                      key={e.entityClass}
                      variant={e.entityClass === entityClass ? 'primary' : 'secondary'}
                      disabled={busy || jevBusy}
                      onClick={() => {
                        reset();
                        setEntityClass(e.entityClass);
                      }}
                    >
                      {e.label}
                    </V2Button>
                  ))}
                </div>
                {entity ? (
                  <>
                    <Alert tone="info">
                      ستون‌های الزامی: {entity.requiredFields.join('، ')}. ستون‌های مجاز:{' '}
                      {entity.writableFields.join('، ')}.{' '}
                      {entity.commitStrategy === 'all-or-nothing'
                        ? 'این نوع داده یکجا ثبت می‌شود؛ با وجود حتی یک خطا هیچ ردیفی ثبت نخواهد شد.'
                        : 'ردیف‌های سالم ثبت می‌شوند و ردیف‌های دارای خطا رد می‌شوند.'}
                    </Alert>
                    <div
                      style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <input
                        ref={inputRef}
                        type="file"
                        disabled={busy || jevBusy}
                        accept=".csv,.xlsx"
                        style={{ display: 'none' }}
                        onChange={(ev) => {
                          const chosen = ev.target.files?.[0];
                          if (chosen) void onFileChosen(chosen);
                        }}
                      />
                      <V2Button
                        variant="secondary"
                        disabled={busy || jevBusy}
                        onClick={() => inputRef.current?.click()}
                      >
                        انتخاب فایل (csv یا xlsx، حداکثر {maxMb.toLocaleString('fa-IR')} مگابایت)
                      </V2Button>
                      <V2Button
                        variant="ghost"
                        disabled={busy || jevBusy}
                        onClick={() =>
                          triggerBlobDownload(
                            sampleCsvBlob(entity),
                            `${entity.entityClass}-sample.csv`,
                          )
                        }
                      >
                        دانلود فایل نمونه
                      </V2Button>
                      {entity.entityClass === 'party' ? (
                        <V2Button
                          variant="ghost"
                          disabled={busy || jevBusy}
                          onClick={async () => {
                            setBusy(true);
                            setError(null);
                            try {
                              triggerBlobDownload(
                                await buildPartyTemplateBlob(entity),
                                'party-template-fa-v1.xlsx',
                              );
                            } catch {
                              setError('ساخت قالب Excel ممکن نشد؛ دوباره تلاش کنید.');
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          دانلود قالب فارسی طرف‌حساب
                        </V2Button>
                      ) : null}
                      {file ? <span style={{ fontSize: 13 }}>{file.name}</span> : null}
                    </div>
                    {reading ? <LoadingState /> : null}
                    {file && headers.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <Alert tone="info" title="نگاشت ستون‌ها">
                          برای هر فیلد، ستون متناظر فایل را انتخاب کنید. فقط نام‌های فنی یکسان از
                          پیش وصل شده‌اند؛ هدرهای فارسی باید دستی وصل شوند. فیلدهای اختیاری بدون
                          ستون وارد نمی‌شوند.
                          {sheetCount > 1
                            ? ` این فایل ${sheetCount.toLocaleString('fa-IR')} شیت دارد؛ فقط شیت اول خوانده می‌شود.`
                            : ''}
                        </Alert>
                        <DatasetJevMapping
                          key={`${entity.entityClass}:${fileReadGeneration.current}`}
                          base={base}
                          entity={entity}
                          headers={headers}
                          mapping={mapping}
                          disabled={busy || reading}
                          onApply={onMappingChanged}
                          onBusyChange={setJevBusy}
                        />
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
                            gap: 12,
                          }}
                        >
                          {entity.writableFields.map((field) => (
                            <label
                              key={field}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                fontSize: 13,
                              }}
                            >
                              <span>
                                {datasetFieldLabel(field)} <span dir="ltr">({field})</span>
                                {entity.requiredFields.includes(field) ? ' — الزامی' : ''}
                              </span>
                              <select
                                aria-label={`ستون فایل برای ${field}`}
                                value={mapping[field] ?? ''}
                                disabled={busy || jevBusy}
                                onChange={(ev) => onMappingChanged(field, ev.target.value)}
                                style={{
                                  padding: 8,
                                  width: '100%',
                                  background: 'var(--sk-surface)',
                                  color: 'var(--sk-fg)',
                                  border: '1px solid var(--sk-border)',
                                  borderRadius: 8,
                                }}
                              >
                                <option value="">بدون ستون / وارد نشود</option>
                                {headers.map((header) => (
                                  <option key={header} value={header}>
                                    {header}
                                  </option>
                                ))}
                              </select>
                            </label>
                          ))}
                        </div>
                        {ignoredHeaders.length > 0 ? (
                          <Alert tone="warning">
                            ستون‌های نادیده‌گرفته‌شده: {ignoredHeaders.join('، ')}
                          </Alert>
                        ) : null}
                        {mappingErrors.length > 0 ? (
                          <Alert tone="danger" title="نگاشت کامل نیست">
                            {mappingErrors.map((message) => (
                              <div key={message}>{message}</div>
                            ))}
                          </Alert>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </>
            )}
            {unavailable.length > 0 ? (
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', lineHeight: 1.9 }}>
                {unavailable.map((e) => (
                  <div key={e.entityClass}>
                    {e.label}:{' '}
                    {e.availability === 'ready' && UI_CLOSED.has(e.entityClass)
                      ? 'فعلاً در دسترس نیست'
                      : e.availability === 'ready'
                        ? 'دسترسی ندارید'
                        : e.availability === 'blocked'
                          ? 'فعلاً در دسترس نیست'
                          : 'در این نسخه پشتیبانی نمی‌شود'}
                  </div>
                ))}
              </div>
            ) : null}
            {error ? (
              <Alert tone="danger" title="خطا">
                {error}
              </Alert>
            ) : null}
            <div style={{ display: 'flex', gap: 8 }}>
              <V2Button variant="ghost" disabled={busy || jevBusy} onClick={onBack}>
                بازگشت
              </V2Button>
              <V2Button
                variant="primary"
                disabled={
                  busy || jevBusy || reading || !file || !entity || mappingErrors.length > 0
                }
                onClick={() => void onCheck()}
              >
                {busy ? 'در حال بررسی…' : 'بررسی فایل'}
              </V2Button>
            </div>
          </div>
        </V2Card>
      ) : null}

      {step === 'review' && dryRun && entity ? (
        <V2Card>
          <V2CardHeader title={`نتیجه بررسی — ${entity.label}`} />
          <div
            style={{ padding: '4px 20px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <V2Badge tone="neutral">کل: {dryRun.rowCount.toLocaleString('fa-IR')}</V2Badge>
              <V2Badge tone="success">معتبر: {dryRun.validCount.toLocaleString('fa-IR')}</V2Badge>
              <V2Badge tone="warn">هشدار: {dryRun.warningCount.toLocaleString('fa-IR')}</V2Badge>
              <V2Badge tone={dryRun.errorCount > 0 ? 'danger' : 'neutral'}>
                خطا: {dryRun.errorCount.toLocaleString('fa-IR')}
              </V2Badge>
            </div>
            <Alert tone="info">
              این مرحله فقط بررسی است و هیچ داده‌ای ثبت نشده است. با «ثبت نهایی» تعداد بالا تأیید و
              ثبت می‌شود.
            </Alert>
            {allOrNothingBlocked ? (
              <Alert tone="danger" title="ثبت ممکن نیست">
                این نوع داده یکجا ثبت می‌شود و فایل شما خطا دارد. فایل را اصلاح و دوباره بارگذاری
                کنید.
              </Alert>
            ) : null}
            {problemTableRows.length > 0 ? (
              <V2Table columns={rowColumns} rows={problemTableRows} zebra />
            ) : null}
            {dryRun.errorCount + dryRun.warningCount > 0 && !allOrNothingBlocked ? (
              <label style={{ display: 'flex', gap: 8, fontSize: 13, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(ev) => setAcknowledged(ev.target.checked)}
                />
                {dryRun.errorCount.toLocaleString('fa-IR')} خطا و{' '}
                {dryRun.warningCount.toLocaleString('fa-IR')} هشدار را دیده‌ام و ادامه می‌دهم
              </label>
            ) : null}
            {error ? (
              <Alert tone="danger" title="خطا">
                {error}
              </Alert>
            ) : null}
            <div style={{ display: 'flex', gap: 8 }}>
              <V2Button variant="ghost" disabled={busy || jevBusy} onClick={reset}>
                انصراف
              </V2Button>
              <V2Button
                variant="primary"
                disabled={
                  busy ||
                  allOrNothingBlocked ||
                  dryRun.validCount + dryRun.warningCount === 0 ||
                  (dryRun.errorCount + dryRun.warningCount > 0 && !acknowledged)
                }
                onClick={() => void onCommit()}
              >
                {busy ? 'در حال ثبت…' : 'ثبت نهایی'}
              </V2Button>
            </div>
          </div>
        </V2Card>
      ) : null}

      {step === 'result' && result ? (
        <V2Card>
          <V2CardHeader title="نتیجه ورود داده" />
          <div
            style={{ padding: '4px 20px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <V2Badge tone="success">
                ثبت‌شده: {result.committedCount.toLocaleString('fa-IR')}
              </V2Badge>
              <V2Badge tone="warn">رد شده: {result.skippedCount.toLocaleString('fa-IR')}</V2Badge>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <V2Button variant="secondary" onClick={reset}>
                ورود فایل جدید
              </V2Button>
              <V2Button variant="ghost" onClick={onBack}>
                بازگشت به فهرست
              </V2Button>
            </div>
          </div>
        </V2Card>
      ) : null}
    </div>
  );
}
